import {Injectable} from "@angular/core";
import {ScreenAccess} from "../interfaces/rest/ScreenAccess";
import {ScreenAccessRepository} from "../repositories/screen-access.repository";
import {LineRepository} from "../repositories/line.repository";
import {Observable, of} from "rxjs";
import {UserSessionRepository} from "../repositories/user-session.repository";
import {ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot} from "@angular/router";
import {StringIndex} from "../interfaces/generic/StringIndex";
import {Helper} from "../helpers/Helper";

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {

  public static MAIN_APP_COMPONENT = 'AppComponent';

  public static USER_TYPE_SEE = 'SEE_USER';
  public static USER_TYPE_EXTERNAL = 'EXTERNAL_USER';

  public static ROLE_SOLUTION_CENTER = 'solution_center_see_';
  public static ROLE_DIGITAL_DEVELOPMENT = 'r_d_seed_see_';
  public static ROLE_FIELD_SERVICE = 's_a_technician_see_';
  public static ROLE_SALES = 'sales_see_';

  public static ROLE_CUSTOMER_ADMIN = 'customer_admin_external_';

  private screenAccessMap: Map<string, ScreenAccess> = new Map();
  private fallbackScreenAccessMap: Map<string, ScreenAccess> = new Map(); // Locally-defined access.
  private lineDataMap: Map<string, {}> = new Map();

  constructor(private screenAccessRepository: ScreenAccessRepository,
              private lineRepository: LineRepository,
              private userSessionRepository: UserSessionRepository) {
    this.initFallbackScreenAccess();
  }

  initializeScreenAccess(accessName: string): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      if(this.screenAccessMap.has(accessName)) {
        subscriber.next(true);
        subscriber.complete();
      }
      else {
        if(!this.userSessionRepository.isLoggedIn()) {
          subscriber.next(false);
          subscriber.complete();
        }
        else {
          this.screenAccessRepository.getScreenAccessAndItsChildren(accessName).subscribe({
            next: (screenAccessList) => {
              if(screenAccessList && screenAccessList.length > 0) {
                screenAccessList.forEach((sa) => {
                  if(sa.name) {
                    this.screenAccessMap.set(sa.name, sa);
                  }
                });
              }

              subscriber.next(true);
              subscriber.complete();
            },
            error: (error) => {
              subscriber.next(false);
              subscriber.complete();
            }
          });
        }
      }
    });
  }

  getScreenAccess(accessName: string) {
    let result = this.screenAccessMap.get(accessName);

    if(!result) {
      result = this.fallbackScreenAccessMap.get(accessName);

      if(result) {
        console.log(`Using fallback screen access: ${accessName}`);
      }
    }

    return result;
  }

  getUserType(): string {
    if(this.userSessionRepository.getUserSession()?.userType === 'see') {
      return AccessControlService.USER_TYPE_SEE;
    }
    else {
      return AccessControlService.USER_TYPE_EXTERNAL;
    }
  }

  getAccessNameFromRoute(route: ActivatedRoute|ActivatedRouteSnapshot): string {
    if(!route) {
      return '';
    }

    if(route instanceof ActivatedRoute) {
      route = route.snapshot;
    }

    if(route.data && route.data['accessName']) {
      return route.data['accessName'];
    }

    return AccessControlService.MAIN_APP_COMPONENT;
  }

  initializeLineData(lineId: number|null, accessName: string): Observable<boolean> {
    if(lineId === null || lineId === undefined || isNaN(lineId)) {
      return of(true);
    }

    return new Observable<boolean>((subscriber) => {
      this.lineRepository.getLine(lineId).subscribe({
        next: (line) => {
          const lineData = {} as StringIndex;
          lineData['lineId'] = line.lineId;
          lineData['lineTier'] = line.lineTier;

          this.lineDataMap.set(accessName, lineData);

          subscriber.next(true);
          subscriber.complete();
        },
        error: (error) => {
          subscriber.next(false);
          subscriber.complete();
        }
      });
    });
  }

  checkOwnUserAccess(route: ActivatedRoute|ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if(route instanceof ActivatedRoute) {
      route = route.snapshot;
    }

    if(route.data && route.data['ownUserRouteIndex']) {
      const ownUserRouteIndex = Number(route.data['ownUserRouteIndex']);
      const ownUserIdFromRoute = state.url.split('/')[ownUserRouteIndex];

      return ownUserIdFromRoute === this.userSessionRepository.getUserSession()?.userId?.toString();
    }

    return false;
  }

  getLineIdFromRoute(route: ActivatedRoute|ActivatedRouteSnapshot, state: RouterStateSnapshot): number|null {
    if(route instanceof ActivatedRoute) {
      route = route.snapshot;
    }

    if(route.data && route.data['lineIdIndex']) {
      const lineIdIndex = Number(route.data['lineIdIndex']);
      const lineIdFromRoute = state.url.split('/')[lineIdIndex];

      return Number(lineIdFromRoute);
    }

    return null;
  }

  checkUserAccess(accessName: string, parentAccessName: string|ActivatedRoute|null = null): boolean {
    if(!accessName) {
      return true;
    }

    if(parentAccessName instanceof ActivatedRoute) {
      parentAccessName = this.getAccessNameFromRoute(parentAccessName);
    }

    if(parentAccessName) {
      accessName = parentAccessName + '.' + accessName;
    }

    console.debug(`Checking access: ${accessName}`);

    const userSession = this.userSessionRepository.getUserSession();

    if(!userSession) {
      return false;
    }

    const userId = userSession.userId;
    const userRoles = userSession.roles;
    const userType = this.getUserType();

    if(!userId || !userRoles) {
      return false;
    }

    const screenAccess = this.getScreenAccess(accessName);

    if(!screenAccess) {
      console.debug(`Undefined access: ${accessName}`);
      return true;
    }
    else {
      let matched = false;

      if((!screenAccess.userTypes || screenAccess.userTypes.length === 0) &&
        (!screenAccess.userRoles || screenAccess.userRoles.length === 0) &&
        (!screenAccess.users || screenAccess.users.length === 0) && !screenAccess.localOnly) {
        matched = true;
      }

      if(!matched && screenAccess.userTypes && screenAccess.userTypes.length > 0) {
        const matchedUserType = screenAccess.userTypes.find((_userType) => _userType === userType);
        if(matchedUserType) {
          matched = true;
        }
      }

      if(!matched && screenAccess.userRoles && screenAccess.userRoles.length > 0) {
        const matchedRole = screenAccess.userRoles.find((role) => userRoles.indexOf(role.keyName!) > -1)
        if(matchedRole) {
          matched = true;
        }
      }

      if(!matched && screenAccess.users && screenAccess.users.length > 0) {
        const matchedUser = screenAccess.users.find((user) => user.userId === userId);
        if(matchedUser) {
          matched = true;
        }
      }

      if (!matched && screenAccess.localOnly) {
        if (Helper.isLocal()) {
          matched = true;
        }
      }

      if(!matched && screenAccess.lineTiers && screenAccess.lineTiers.length > 0) {
        const matchedLineTier = screenAccess.lineTiers.find((lineTier) => {
          if(parentAccessName) {
            return (this.lineDataMap.get(parentAccessName) as StringIndex)['lineTier'] === lineTier;
          }
          else {
            return (this.lineDataMap.get(accessName) as StringIndex)['lineTier'] === lineTier;
          }
        });

        if(matchedLineTier) {
          matched = true;
        }
      }

      if(matched) {
        console.debug(`Granting access for ${accessName}`);
        return true;
      }
      else {
        console.log(`Denying access for ${accessName}`);
        return false;
      }
    }
  }

  checkUserRole(role: string): boolean {
    const userSession = this.userSessionRepository.getUserSession();

    if(userSession) {
      const currentUserRoles = userSession.roles;
      if(currentUserRoles) {
        if(currentUserRoles.indexOf(role) > -1) {
          return true;
        }
      }
    }

    return false;
  }

  protected initFallbackScreenAccess(): void {
    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.clients',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.plants',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.lines',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.assets',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.gateways',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.shifts',
      userTypes: [AccessControlService.USER_TYPE_SEE],
      userRoles: [
        {
          keyName: AccessControlService.ROLE_CUSTOMER_ADMIN,
        }
      ]
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.seeUsers',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.externalUsers',
      userTypes: [AccessControlService.USER_TYPE_SEE],
      userRoles: [
        {
          keyName: AccessControlService.ROLE_CUSTOMER_ADMIN,
        }
      ]
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.rooms',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.lineSegments',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.rejectGroups',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.cmsVideos',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.cmsHeaders',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.languages',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.regions',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.errorCodes',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.uom',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.tileTemplateTranslations',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.alertTranslations',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.navigation.assetTypeParameterTranslations',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.assetTypes',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.tileLibrary',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.remedies',
      userTypes: [AccessControlService.USER_TYPE_SEE]
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.graphics',
      userTypes: [AccessControlService.USER_TYPE_SEE],
      userRoles: [
        {
          keyName: AccessControlService.ROLE_CUSTOMER_ADMIN,
        }
      ]
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.languages',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.regions',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'AppComponent.administration.translations',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'ReportFormView_add',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'ReportFormView_edit',
      userTypes: [AccessControlService.USER_TYPE_SEE],
    });

    this.addFallbackScreenAccess({
      name: 'ReportsListComponent.upgradeHierarchy',
      localOnly: true,
    });
  }

  protected addFallbackScreenAccess(screenAccess: ScreenAccess): void {
    if(screenAccess.name) {
      this.fallbackScreenAccessMap.set(screenAccess.name, screenAccess);
    }
  }
}
